import React, { useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
} from "@panda0/panda0-ui";
import { DollarSign, EuroIcon, Gift, Notebook, PoundSterlingIcon } from "lucide-react";
import { Controller, useFormContext } from "react-hook-form";

import { FormData } from "../types";
import { cn } from "@panda0/panda0-utils";

const ReferralRewardsEditor: React.FC = () => {
  const { control, watch } = useFormContext<FormData>();
  const referralRewards = watch("referralRewards");
  const [enabled, setEnabled] = React.useState(true);

  const currencyOptions = useMemo(() => [
    {
      value: "USD",
      label: "USD",
      icon: <DollarSign className="h-4 w-4" />,
      symbol: "$",
    },
    {
      value: "EUR",
      label: "EUR",
      icon: <EuroIcon className="h-4 w-4" />,
      symbol: "€",
    },
    {
      value: "GBP",
      label: "GBP",
      icon: <PoundSterlingIcon className="h-4 w-4" />,
      symbol: "£",
    },
    { value: "INR", label: "INR", icon: "₹", symbol: "₹" }, // Using text for INR since no built-in icon
  ], []);

  const rewardOptions = useMemo(() => [
    {
      value: "CASH",
      label: "CASH",
      icon: <DollarSign className="h-4 w-4" />,
    },
    {
      value: "COUPONS",
      label: "COUPONS",
      icon: <Notebook className="h-4 w-4" />,
    },
  ], []);

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Configure referral programs and rewards for this job posting.
      </p>

      <div className="flex items-center justify-between mb-4">
        <label className="font-medium">Enable Referral Program</label>
        <Switch checked={enabled} fn={setEnabled} />
      </div>

      {enabled && (
        <div className="space-y-4">
          <div className="p-4 border rounded-md bg-gray-50">
            <div className="flex items-center mb-4">
              <Gift className="h-5 w-5 mr-2 text-primary" />
              <h3 className="font-medium">Reward Configuration</h3>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium">Reward Type</label>
                <div className="flex gap-2">
                  {rewardOptions.map((option) => (
                    <Controller
                      name="referralRewards.REFERRAL_REWARDTYPE"
                      control={control}
                      render={({ field }) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() =>
                            field.onChange(option.value)
                          }
                          className={cn(
                            "flex items-center justify-center w-12 h-10 rounded-md border transition-all",
                            referralRewards?.REFERRAL_REWARDTYPE === option.value
                              ? "bg-black border-primary text-white font-medium"
                              : "border-gray-300 hover:border-gray-400 text-gray-700 hover:bg-gray-50"
                          )}
                        >
                          {typeof option.icon === "string" ? option.icon : option.icon}
                        </button>
                      )} />
                  ))}
                </div>
              </div>

              {referralRewards?.REFERRAL_REWARDTYPE === "CASH" && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium">
                    Reward Amount
                  </label>
                  <div className="flex gap-2">
                    {currencyOptions.map((option) => (
                      <Controller
                        name="referralRewards.REFERRAL_CURRENCY"
                        control={control}
                        render={({ field }) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() =>
                              field.onChange(option.value)
                            }
                            className={cn(
                              "flex items-center justify-center w-12 h-10 rounded-md border transition-all",
                              referralRewards?.REFERRAL_CURRENCY === option.value
                                ? "bg-black border-primary text-white font-medium"
                                : "border-gray-300 hover:border-gray-400 text-gray-700 hover:bg-gray-50"
                            )}
                          >
                            {typeof option.icon === "string" ? option.icon : option.icon}
                          </button>
                        )} />
                    ))}
                    <Controller
                      name="referralRewards.REFERRAL_AMOUNT"
                      control={control}
                      render={({ field }) => (
                        <input
                          type="number"
                          className="flex-1 border rounded-md p-2"
                          placeholder="Amount"
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      )}
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <label className="block text-sm font-medium">
                  Reward Details
                </label>
                <Controller
                  name="referralRewards.REFERRAL_DETAILS"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      placeholder="Describe the referral rewards in detail..."
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <div className="p-4 border rounded-md bg-gray-50">
            <h3 className="font-medium mb-4">Program Rules</h3>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm">Referral must be hired</label>
                <Controller
                  name="referralRewards.REFERRAL_MUSTBEHIRED"
                  control={control}
                  defaultValue={true}
                  render={({ field }) => (
                    <input
                      type="checkbox"
                      checked={field.value === undefined ? true : field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      className="rounded"
                    />
                  )}
                />
              </div>

              <div className="flex items-center justify-between">
                <label className="text-sm">
                  Referral must complete probation
                </label>
                <Controller
                  name="referralRewards.REFERRAL_MUSTCOMPLETEPROBATION"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <input
                      type="checkbox"
                      checked={field.value || false}
                      onChange={(e) => field.onChange(e.target.checked)}
                      className="rounded"
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(ReferralRewardsEditor);
